import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    setDoc
} from 'firebase/firestore';
import { db } from './firebase';

const PRICING_COLLECTION = 'pricing';

/**
 * Subscribe to all pricing with real-time updates
 */
export const subscribeToPricing = (callback) => {
  const q = query(
    collection(db, PRICING_COLLECTION),
    orderBy('subject', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const prices = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(prices);
  });
};

/**
 * Fetch all pricing (one-time fetch)
 */
export const fetchPricing = async () => {
  try {
    const q = query(
      collection(db, PRICING_COLLECTION),
      orderBy('subject', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching pricing:', error);
    throw error;
  }
};

/**
 * Add new pricing
 */
export const addPricing = async (pricingData) => {
  try {
    const docRef = await addDoc(collection(db, PRICING_COLLECTION), {
      ...pricingData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding pricing:', error);
    throw error;
  }
};

export const seedMissingSubjects = async (existingPrices) => {
  const ALL_SUBJECTS = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English Language',
    'Further Mathematics', 'Agricultural Science', 'Economics', 'Accounting',
    'Financial Accounting', 'Marketing', 'Literature in English', 'Government'
  ];

  const existingSubjectNames = existingPrices.map(p => p.subject);
  const missingSubjects = ALL_SUBJECTS.filter(s => !existingSubjectNames.includes(s));

  try {
    for (const subject of missingSubjects) {
      await addDoc(collection(db, PRICING_COLLECTION), {
        subject,
        monthly: 1000,
        termly: 3000,
        annual: 10000,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
    // Also update existing subjects to 1000 if they aren't already, but user might just want the new ones added or all set to 1000. Let's set all to 1000.
    for (const p of existingPrices) {
      if (p.monthly !== 1000) {
        await updatePricing(p.id, {
          monthly: 1000,
          termly: 3000,
          annual: 10000,
        });
      }
    }
  } catch (error) {
    console.error('Error seeding subjects:', error);
    throw error;
  }
};

/**
 * Update pricing
 */
export const updatePricing = async (pricingId, updates) => {
  try {
    const pricingRef = doc(db, PRICING_COLLECTION, pricingId);
    await updateDoc(pricingRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating pricing:', error);
    throw error;
  }
};

/**
 * Delete pricing
 */
export const deletePricing = async (pricingId) => {
  try {
    const pricingRef = doc(db, PRICING_COLLECTION, pricingId);
    await deleteDoc(pricingRef);
  } catch (error) {
    console.error('Error deleting pricing:', error);
    throw error;
  }
};

/**
 * Calculate pricing statistics
 */
export const calculatePricingStats = (pricing) => {
  const totalSubjects = pricing.length;
  
  // Calculate average prices
  const avgMonthly = pricing.reduce((sum, p) => sum + (p.monthly || 0), 0) / totalSubjects || 0;
  const avgTermly = pricing.reduce((sum, p) => sum + (p.termly || 0), 0) / totalSubjects || 0;
  const avgAnnual = pricing.reduce((sum, p) => sum + (p.annual || 0), 0) / totalSubjects || 0;

  // Find min and max prices
  const monthlyPrices = pricing.map(p => p.monthly || 0).filter(p => p > 0);
  const termlyPrices = pricing.map(p => p.termly || 0).filter(p => p > 0);
  const annualPrices = pricing.map(p => p.annual || 0).filter(p => p > 0);

  return {
    totalSubjects,
    averages: {
      monthly: Math.round(avgMonthly),
      termly: Math.round(avgTermly),
      annual: Math.round(avgAnnual),
    },
    ranges: {
      monthly: {
        min: Math.min(...monthlyPrices) || 0,
        max: Math.max(...monthlyPrices) || 0,
      },
      termly: {
        min: Math.min(...termlyPrices) || 0,
        max: Math.max(...termlyPrices) || 0,
      },
      annual: {
        min: Math.min(...annualPrices) || 0,
        max: Math.max(...annualPrices) || 0,
      },
    },
  };
};

/**
 * Subscribe to global pricing settings (bundles, free previews, etc.)
 */
export const subscribeToPricingSettings = (callback) => {
  const docRef = doc(db, 'settings', 'pricing_bundles');
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data());
    } else {
      // Return defaults if not set yet
      callback({
        singleSubjectBase: { price: 1000, active: true },
        threeSubjects: { price: 2500, discount: 0, active: true },
        sixSubjects: { price: 4000, discount: 0, active: true },
        utmeBundle: { price: 3500, discount: 0, active: true },
        freePreviews: { lessons: 3, quizzes: 1 },
        termPricing: {
          singleSubject: { price: 2500, active: true },
          threeSubjects: { price: 6500, active: true },
          sixSubjects: { price: 10000, active: true },
        }
      });
    }
  });
};

/**
 * Update global pricing settings
 */
export const updatePricingSettings = async (updates) => {
  try {
    const docRef = doc(db, 'settings', 'pricing_bundles');
    await setDoc(docRef, updates, { merge: true });
  } catch (error) {
    console.error('Error updating pricing settings:', error);
    throw error;
  }
};

export default {
  subscribeToPricing,
  fetchPricing,
  addPricing,
  updatePricing,
  deletePricing,
  calculatePricingStats,
  subscribeToPricingSettings,
  updatePricingSettings,
};
